import { isEmpty, get, last } from 'lodash'
import {
  UNDO_REDO_LABELS,
  deletePattern,
  getPatternDefForPage,
  updatePagePattern,
  getPatternDef,
  updatePatternDef,
  runTransactionWithRetry,
  addOrUpdateRouterWithPattern,
  setPageTitle,
  mergeSeoMetaTags,
  getCollection,
  deletePageLinkFields,
  updateOrCreatePageLinkField,
  parseUrl,
  digestSchema,
  isClassicEditor,
  DYNAMIC_PAGE_TYPES,
  PAGE_TYPES,
  setDynamicPageTypeStateForPage,
  truncateCollectionName,
  isEditor3OrStudio2,
} from '@wix/wix-data-client-common'
import { DATA_BINDING } from '@wix/app-definition-ids'
import {
  fetchAllRouterDatasetRefsFromPage,
  fetchRouterDatasetFromPage,
} from './fetchRouterDatasetFromPage'
import {
  getProgressBar,
  openPagesPanelIfNeeded,
} from '../progress-bar/executeSteps'
import { getPresetDefinitionById } from '../presets/getPresetDefinitionById'
import { getFedopsInteractionName } from './getFedopsInteractionName'
import { getDpStepBuilder } from './add-dynamic-pages/dpCreationSteps'
import { prepareDataForCollection } from './add-dynamic-pages/flows/conversion/prepareDataForCollection'
import { handleFirstTimeDpConversion } from './isFirstTimeConversionHandler'
import { getPageToNavigateAfterRemove } from './getPageToNavigateAfterRemove'
import { shouldUseSeoV2RouteConfig } from './useSeoV2Infra'
import { openInspectorForPageType } from './openInspectorPanelForPageType'
import { cleanUpPresetSeo } from '../presets/cleanUpPresetSeo'

import type {
  AppApi,
  DpBiDefaults,
  DpPageType,
  DynamicPageType,
  EDITOR_TYPE,
  Features,
  PageToAdd,
} from '@wix/wix-data-client-common'
import type {
  EditorSDK,
  FedopsLogger,
  I18n,
  Logger,
  PatternDefinition,
  RouterConfig,
} from '@wix/wix-data-client-common-standalone'
import { createError } from '@wix/wix-data-client-common-standalone'
import type {
  PageRef,
  RouterData,
  RouterPage,
  RouterRef,
} from '@wix/editor-platform-sdk-types'
import type { CollectionsApi, Patch } from '@wix/wix-code-collections-api'
import type { WixDataApi } from '@wix/wix-data-core'
import type Experiments from '@wix/wix-experiments'
import type { HttpClient } from '@wix/http-client'
import type { PublishingStatus, Schema } from '@wix/wix-data-schema-types'
import { getDynamicPageType } from './getDynamicPageType'

const pageKeytoType: Record<DpPageType, DynamicPageType> = {
  itemPage: DYNAMIC_PAGE_TYPES.ITEM_PAGE,
  indexPage: DYNAMIC_PAGE_TYPES.INDEX_PAGE,
}

const getRouteData = async ({
  editorSdkProxy,
  pageRef,
  routerRef,
}: {
  editorSdkProxy: EditorSDK
  pageRef: PageRef
  routerRef: RouterRef
}) => {
  const router = await editorSdkProxy.routers.get<RouterConfig>({ routerRef })

  const { pattern, def: patternDef } = getPatternDefForPage(
    router.config,
    router.pages,
    pageRef,
  )

  return { router, pattern, patternDef }
}

const updateRouter = async ({
  editorSdkProxy,
  pageRef,
  routerRef,
  router,
  pattern,
}: {
  editorSdkProxy: EditorSDK
  pageRef: PageRef
  routerRef: RouterRef
  router: RouterData & { config: RouterConfig }
  pattern: string
}) => {
  await editorSdkProxy.routers.pages.disconnect({ routerRef, pageRef })

  const isLastPage = router.pages.length === 1
  if (isLastPage) {
    await editorSdkProxy.routers.remove({ routerRef })
  } else {
    await editorSdkProxy.routers.update({
      routerRef,
      config: deletePattern(router.config, pattern),
    })
  }
}

const updatePageData = async ({
  editorSdkProxy,
  pageRef,
  routerPrefix,
  editorType,
}: {
  editorSdkProxy: EditorSDK
  pageRef: PageRef
  routerPrefix?: string
  editorType: EDITOR_TYPE
}) => {
  const { managingAppDefId } = await getPageData(editorSdkProxy, pageRef)

  if (managingAppDefId === DATA_BINDING) {
    const pageDataUpdates = {
      managingAppDefId: undefined,
      // Classic Editor gracefully handles duplicate pageUriSEO by adding a suffix to the provided value,
      // whereas Studio Editor throws an error in such cases. Therefore, the same solution cannot be applied in Studio.
      ...(routerPrefix && isClassicEditor(editorType)
        ? { pageUriSEO: routerPrefix }
        : {}),
    }

    await editorSdkProxy.document.pages.data.update({
      pageRef,
      data: pageDataUpdates,
    })
  }
}

const removeAllRouterDatasetsFromPage = async ({
  editorSdkProxy,
  appApi,
  pageRef,
}: {
  editorSdkProxy: EditorSDK
  appApi: AppApi
  pageRef: PageRef
}) => {
  const routerDatasetRefs = await fetchAllRouterDatasetRefsFromPage(
    editorSdkProxy,
    pageRef,
  )
  for (const routerDatasetRef of routerDatasetRefs) {
    await appApi.removeRouterDatasetController({
      datasetRef: routerDatasetRef,
    })
  }
  appApi.emit('datasetRemoved')
}

export const disconnectDynamicPage = async ({
  collectionsApi,
  editorSdkProxy,
  routerRef,
  pageRef,
  appApi,
  editorType,
}: {
  collectionsApi: CollectionsApi
  editorSdkProxy: EditorSDK
  routerRef?: RouterRef
  pageRef: PageRef
  appApi: AppApi
  editorType: EDITOR_TYPE
}) => {
  if (routerRef) {
    const { router, pattern, patternDef } = await getRouteData({
      editorSdkProxy,
      pageRef,
      routerRef,
    })

    await runTransactionWithRetry(editorSdkProxy, async () => {
      await updateRouter({
        editorSdkProxy,
        pageRef,
        routerRef,
        router,
        pattern,
      })

      await updatePageData({
        editorSdkProxy,
        pageRef,
        routerPrefix: router.prefix,
        editorType,
      })

      await removeAllRouterDatasetsFromPage({ editorSdkProxy, appApi, pageRef })
    })

    const collection = getCollection(patternDef)
    await deletePageLinkFields(
      collectionsApi,
      collection,
      router.prefix,
      pattern,
    )
  } else {
    await runTransactionWithRetry(editorSdkProxy, async () => {
      await updatePageData({
        editorSdkProxy,
        pageRef,
        editorType,
      })

      await removeAllRouterDatasetsFromPage({ editorSdkProxy, appApi, pageRef })
    })
  }

  editorSdkProxy.editor.routers.refresh().catch(() => {}) // Editor X does not have implementation for it yet

  await editorSdkProxy.history.add({
    label: UNDO_REDO_LABELS.DISCONNECT_PAGE_FROM_DATA_BINDING_ROUTER,
  })
}

export const deleteDynamicPage = async ({
  collectionsApi,
  editorSdkProxy,
  pageRef,
  editorType,
  appApi,
}: {
  collectionsApi: CollectionsApi
  editorSdkProxy: EditorSDK
  pageRef: PageRef
  editorType: EDITOR_TYPE
  appApi: AppApi
}) => {
  const routerRef = await editorSdkProxy.routers.getByPage({ pageRef })
  const router = await editorSdkProxy.routers.get<RouterConfig>({ routerRef })
  const pageToNavigateAfterRemove = await getPageToNavigateAfterRemove({
    editorSdkProxy,
    router,
    pageRef,
    editorType,
  })

  const { pattern, def: patternDef } = getPatternDefForPage(
    router.config,
    router.pages,
    pageRef,
  )

  await runTransactionWithRetry(editorSdkProxy, async () => {
    await editorSdkProxy.pages.navigateTo({
      pageRef: pageToNavigateAfterRemove,
    })

    await editorSdkProxy.routers.pages.remove({
      routerRef,
      pageRef,
      pageToNavigateAfterRemove,
    })

    const isLastPage = router.pages.length === 1
    if (isLastPage) {
      await editorSdkProxy.routers.remove({ routerRef })
    } else {
      await editorSdkProxy.routers.update({
        routerRef,
        config: deletePattern(router.config, pattern),
      })
    }
  })

  if (!patternDef) {
    throw createError(
      'Delete dynamic page did not complete due to missing patternDef',
      {
        data: {
          router,
          pageRef,
          routerRef,
        },
      },
    )
  }

  if (isEditor3OrStudio2(editorType)) {
    await appApi.handleChangedRouters({ pageRefs: [pageRef] })
    await editorSdkProxy.history.add({
      label: UNDO_REDO_LABELS.UPDATE_ROUTERS,
      params: { pageRefs: [pageRef] },
    })
  } else {
    const collection = getCollection(patternDef)
    await deletePageLinkFields(
      collectionsApi,
      collection,
      router.prefix,
      pattern,
    )

    await editorSdkProxy.history.add({
      label: UNDO_REDO_LABELS.REMOVE_PAGE_FROM_DATA_BINDING_ROUTER,
    })
  }
}

const findPageByRole = (pages: RouterPage[], pageRole: string) =>
  pages.find(({ pageRoles }) => pageRoles.includes(pageRole))

const saveNewPrefixAndPattern = async function ({
  editorSdkProxy,
  collectionsApi,
  routerRef,
  pageRef,
  routerConfig,
  collectionId,
  previousPrefix,
  newPrefix,
  previousPattern,
  newPattern,
  routerPages,
  currentPageName,
}: {
  editorSdkProxy: EditorSDK
  collectionsApi: CollectionsApi
  routerRef: RouterRef
  pageRef: PageRef
  routerConfig: RouterConfig
  collectionId: string
  previousPrefix: string
  newPrefix: string
  previousPattern: string
  newPattern: string
  routerPages: RouterPage[]
  currentPageName: string
}) {
  const isLastPage = routerPages.length <= 1
  const updatedRouterConfig = updatePagePattern(
    routerConfig,
    previousPattern,
    newPattern,
  )
  const newPatternDef = getPatternDef(updatedRouterConfig, newPattern)
  const router = await editorSdkProxy.document.routers.get<RouterConfig>({
    routerRef,
  })
  const pageId = get(findPageByRole(router.pages, newPatternDef.pageRole), [
    'pageRef',
    'id',
  ])!

  await updateOrCreatePageLinkField({
    collectionId,
    collectionsApi,
    newDisplayName: currentPageName,
    newPattern,
    newPrefix,
    oldPattern: previousPattern,
    oldPrefix: previousPrefix,
    pageId,
  })

  const { newRouterRef } = await runTransactionWithRetry(
    editorSdkProxy,
    async () => {
      const newRouterRef = await addOrUpdateRouterWithPattern(
        editorSdkProxy,
        newPrefix,
        newPattern,
        newPatternDef,
      )

      await editorSdkProxy.routers.pages
        .move({ pageRef, fromRouterRef: routerRef, toRouterRef: newRouterRef })
        .then(() =>
          isLastPage
            ? editorSdkProxy.routers.remove({ routerRef })
            : editorSdkProxy.routers.update({
                routerRef,
                config: deletePattern(routerConfig, previousPattern),
              }),
        )

      return { newRouterRef }
    },
  )

  return { routerRef: newRouterRef, pageRef }
}

const saveNewPattern = async function ({
  editorSdkProxy,
  collectionsApi,
  routerRef,
  pageRef,
  routerConfig,
  collectionId,
  previousPrefix,
  previousPattern,
  newPattern,
  currentPageName,
}: {
  editorSdkProxy: EditorSDK
  collectionsApi: CollectionsApi
  routerRef: RouterRef
  pageRef: PageRef
  routerConfig: RouterConfig
  collectionId: string
  previousPrefix: string
  previousPattern: string
  newPattern: string
  currentPageName: string
}) {
  const updatedRouterConfig = updatePagePattern(
    routerConfig,
    previousPattern,
    newPattern,
  )
  const newPatternDef = getPatternDef(updatedRouterConfig, newPattern)
  const router = await editorSdkProxy.document.routers.get({ routerRef })
  const pageId = get(findPageByRole(router.pages, newPatternDef.pageRole), [
    'pageRef',
    'id',
  ])

  await updateOrCreatePageLinkField({
    collectionId,
    collectionsApi,
    newDisplayName: currentPageName,
    newPattern,
    newPrefix: previousPrefix,
    oldPattern: previousPattern,
    oldPrefix: previousPrefix,
    pageId: pageId!,
  })

  await editorSdkProxy.routers.update({
    routerRef,
    config: updatedRouterConfig,
  })

  return { routerRef, pageRef }
}

export const updateDynamicPageUrl = async ({
  editorSdkProxy,
  collectionsApi,
  pageRef,
  collectionId,
  newUrl,
}: {
  editorSdkProxy: EditorSDK
  collectionsApi: CollectionsApi
  pageRef: PageRef
  collectionId: string
  newUrl: string
}) => {
  const routerRef = await editorSdkProxy.routers.getByPage({ pageRef })
  const router = await editorSdkProxy.routers.get<RouterConfig>({ routerRef })
  const { pattern: previousPattern } = getPatternDefForPage(
    router.config,
    router.pages,
    pageRef,
  )
  const previousPrefix = router.prefix
  const { prefix: newPrefix, pattern: newPattern } = parseUrl(newUrl)
  const rawSchema = await collectionsApi.get(collectionId)

  if (rawSchema.isDeleted) {
    throw new Error(
      `Cannot update page url, because ${rawSchema.displayName} collection is removed`,
    )
  }

  const collection = digestSchema({ rawSchema })
  const { title: currentPageName } = await editorSdkProxy.pages.getPageData({
    pageRef,
  })

  const result =
    previousPrefix !== newPrefix
      ? await saveNewPrefixAndPattern({
          editorSdkProxy,
          collectionsApi,
          routerRef,
          pageRef,
          routerConfig: router.config,
          collectionId: collection.id,
          previousPrefix,
          newPrefix,
          previousPattern,
          newPattern,
          routerPages: router.pages,
          currentPageName,
        })
      : await saveNewPattern({
          editorSdkProxy,
          collectionsApi,
          routerRef,
          pageRef,
          routerConfig: router.config,
          collectionId: collection.id,
          previousPrefix,
          previousPattern,
          newPattern,
          currentPageName,
        })

  await editorSdkProxy.history.add({
    label: UNDO_REDO_LABELS.UPDATE_DYNAMIC_PAGE_URL,
  })

  editorSdkProxy.editor.routers.refresh().catch(() => {}) // Editor X does not have implementation for it yet

  const pageType = await getDynamicPageType({
    editorSdkProxy,
    pageRef,
  })

  if (pageType) {
    await setDynamicPageTypeStateForPage({
      editorSdkProxy,
      pageRef,
      pageType,
    })
  }

  const appApi = await editorSdkProxy.editor.getAppAPI()
  appApi.emit('dynamicPageUrlChanged', pageRef)

  return result
}

export const updateDynamicPageSEO = async ({
  editorSdkProxy,
  pageRef,
  title,
  metatags,
}: {
  editorSdkProxy: EditorSDK
  pageRef: PageRef
  title: string
  metatags: PatternDefinition['seoMetaTags']
}) => {
  const routerRef = await editorSdkProxy.routers.getByPage({ pageRef })
  const router = await editorSdkProxy.routers.get<RouterConfig>({ routerRef })
  const { pattern, def: originalPatternDef } = getPatternDefForPage(
    router.config,
    router.pages,
    pageRef,
  )

  let updatedPatternDef = originalPatternDef
  if (title) {
    updatedPatternDef = setPageTitle(updatedPatternDef, title)
  }
  if (metatags) {
    updatedPatternDef = mergeSeoMetaTags(updatedPatternDef, metatags)
  }

  const updatedRouterConfig = updatePatternDef(
    router.config,
    pattern,
    updatedPatternDef,
  )
  await editorSdkProxy.routers.update({
    routerRef,
    config: updatedRouterConfig,
  })

  await editorSdkProxy.history.add({
    label: UNDO_REDO_LABELS.UPDATE_DYNAMIC_PAGE_SEO_METATAGS,
  })
}

const getPageData = (editorSdkProxy: EditorSDK, pageRef: PageRef) =>
  editorSdkProxy.pages.getPageData({ pageRef })

const getFedopsInteractionKey = ({
  pagesToAdd,
}: {
  pagesToAdd: PageToAdd[]
}) =>
  pagesToAdd[0].key === 'itemPage'
    ? 'addItemPageFromCollection'
    : 'addListPageFromCollection'

const getIsIndexable = async ({
  editorSdkProxy,
  pageRef,
}: {
  editorSdkProxy: EditorSDK
  pageRef: PageRef
}) => {
  const { indexable } = await editorSdkProxy.pages.getPageData({ pageRef })
  return indexable
}

export const getDpCreationFlows = ({
  collectionsApi,
  editorSdkProxy,
  wixData,
  i18n,
  editorType,
  isSandboxEnabled,
  defaultDraftPublishStatus,
  pluginsPatches,
  logger,
  fedopsLogger,
  appApi,
  biDefaults,
  experimentsManager,
  httpClient,
  features,
}: {
  collectionsApi: CollectionsApi
  editorSdkProxy: EditorSDK
  wixData: WixDataApi
  i18n: I18n
  editorType: EDITOR_TYPE
  isSandboxEnabled: boolean
  defaultDraftPublishStatus: PublishingStatus
  pluginsPatches: Patch[]
  logger: Logger
  fedopsLogger: FedopsLogger
  appApi: AppApi
  biDefaults: DpBiDefaults
  experimentsManager: Experiments
  httpClient: HttpClient
  features: Features
}) => {
  const stepBuilder = getDpStepBuilder({
    collectionsApi,
    editorSdkProxy,
    wixData,
    i18n,
    editorType,
    isSandboxEnabled,
    defaultDraftPublishStatus,
    pluginsPatches,
    appApi,
    logger,
    biDefaults,
    experimentsManager,
    httpClient,
  })

  const getInteractionName = getFedopsInteractionName(editorType)

  return {
    addPreset: async ({
      presetId,
      pagesToAdd,
      collectionId,
      collectionName,
      origin,
    }: {
      presetId: string
      pagesToAdd: PageToAdd[]
      collectionId: string
      collectionName: string
      origin: string
    }) => {
      const originalPreset = await getPresetDefinitionById(
        presetId,
        editorType,
        editorSdkProxy,
      )
      const preset = (await shouldUseSeoV2RouteConfig({
        editorSdkProxy,
        experimentsManager,
      }))
        ? cleanUpPresetSeo(originalPreset)
        : originalPreset

      const title = isEmpty(pagesToAdd)
        ? i18n.t('Adding_Collection_Native_Progress_Bar_Title', {
            collection_name: truncateCollectionName(collectionName),
          })
        : i18n.t('Adding_Dynamic_Pages_Native_Progress_Bar_Title')

      const progressBar = getProgressBar({
        editorSdkProxy,
        logger,
        totalSteps: pagesToAdd.length + 1,
        title,
      })

      const interactionKey =
        presetId === 'blank' ? 'addFromScratch' : 'addFromPreset'

      const interactionName = getInteractionName(interactionKey)

      fedopsLogger.interactionStarted(interactionName)

      const result = await stepBuilder.getStepsForAddingPagesFromPreset({
        preset,
        pagesToAdd,
        collectionId,
        collectionName,
        isIndexable: true,
        progressBar,
      })

      if (result.pages.length) {
        for (const page of result.pages) {
          await setDynamicPageTypeStateForPage({
            editorSdkProxy,
            pageRef: page.pageRef,
            pageType:
              page.pageType === PAGE_TYPES.COLLECTION
                ? DYNAMIC_PAGE_TYPES.INDEX_PAGE
                : DYNAMIC_PAGE_TYPES.ITEM_PAGE,
          })
        }
        await openPagesPanelIfNeeded({
          editorSdkProxy,
          origin,
          pageRef: last(result.pageRefs)!,
        })
      }

      fedopsLogger.interactionEnded(interactionName)

      appApi.openContentManager({
        collectionId,
        isModal: true,
        origin,
      })

      return { pageRefs: result.pageRefs }
    },
    addDefaultDynamicPages: async ({
      pagesToAdd,
      collectionId,
      collectionName,
      presetId = 'blank',
      isIndexable = true,
      origin,
    }: {
      pagesToAdd: PageToAdd[]
      collectionId: string
      collectionName: string
      presetId: string
      isIndexable: boolean
      origin: string
    }) => {
      const preset = await getPresetDefinitionById(
        presetId,
        editorType,
        editorSdkProxy,
      )

      const title = i18n.t('Adding_Dynamic_Pages_Native_Progress_Bar_Title')

      const progressBar = getProgressBar({
        editorSdkProxy,
        logger,
        totalSteps: pagesToAdd.length,
        title,
      })

      const interactionKey = getFedopsInteractionKey({ pagesToAdd })

      const interactionName = getInteractionName(interactionKey)

      fedopsLogger.interactionStarted(interactionName)

      const result =
        await stepBuilder.getStepsForAddingDefaultPagesFromExistingCollection({
          preset,
          pagesToAdd,
          collectionId,
          collectionName,
          isIndexable,
          progressBar,
        })

      const pageRef = last(result.pageRefs)!
      const [routerController] = await Promise.all([
        fetchRouterDatasetFromPage(editorSdkProxy, pageRef),
        openPagesPanelIfNeeded({
          editorSdkProxy,
          origin,
          pageRef,
        }),
      ])

      const pageType = pagesToAdd[0]

      await setDynamicPageTypeStateForPage({
        editorSdkProxy,
        pageRef,
        pageType: pageKeytoType[pageType.key],
      })

      const openPanelOnDynamicPageCreation = Boolean(
        features.inspectorXP && routerController,
      )

      if (openPanelOnDynamicPageCreation) {
        await openInspectorForPageType({
          editorSdkProxy,
          appApi,
          pageType,
          routerControllerRef: routerController!.ref,
        })
      }

      fedopsLogger.interactionEnded(interactionName)

      return result
    },
    convertFromCollection: async ({
      pagesToAdd,
      collectionId,
      collectionName,
      origin,
    }: {
      pagesToAdd: PageToAdd[]
      collectionId: string
      collectionName: string
      origin: string
    }) => {
      const presetId = 'blank'

      const pageRef = pagesToAdd.find(page => page.staticPageRef)
        ?.staticPageRef as PageRef

      const [preset, isIndexable, pageData] = await Promise.all([
        getPresetDefinitionById(presetId, editorType, editorSdkProxy),
        getIsIndexable({ editorSdkProxy, pageRef }),
        editorSdkProxy.pages.getPageData({ pageRef }),
      ])

      const interactionName = getInteractionName('convertStaticFromCollection')

      const title = i18n.t('Converting_Dynamic_Pages_Native_Progress_Bar_Title')

      const progressBar = getProgressBar({
        editorSdkProxy,
        logger,
        totalSteps: pagesToAdd.length,
        title,
      })

      fedopsLogger.interactionStarted(interactionName)

      const result =
        await stepBuilder.getStepsForAddingDefaultPagesFromExistingCollection({
          preset,
          pagesToAdd,
          collectionId,
          collectionName,
          isIndexable,
          progressBar,
          pageTitleBase: pageData.title,
        })

      await setDynamicPageTypeStateForPage({
        editorSdkProxy,
        pageRef,
        pageType: DYNAMIC_PAGE_TYPES.ITEM_PAGE,
      })

      await openPagesPanelIfNeeded({ editorSdkProxy, origin, pageRef })

      fedopsLogger.interactionEnded(interactionName)

      return result
    },
    convertFromScratchAndBindComponents: async ({
      pageRef,
      collectionId,
      collectionName,
      fields,
      records,
      bindings,
      origin,
    }: {
      pageRef: PageRef
      collectionId: string
      collectionName: string
      fields: Schema['fields']
      records: Record<string, string>[]
      bindings: PageToAdd['bindings']
      origin: string
    }) => {
      const presetId = 'blank'
      const pagesToAdd: PageToAdd[] = [
        { key: 'itemPage', staticPageRef: pageRef, bindings },
      ]

      const [preset, pageData] = await Promise.all([
        getPresetDefinitionById(presetId, editorType, editorSdkProxy),
        editorSdkProxy.pages.getPageData({ pageRef }),
      ])

      const pageTitle = pageData.title

      const collectionToAdd = prepareDataForCollection({
        fields,
        records,
        collectionId,
        collectionName,
        pageTitle,
      })

      const interactionName = getInteractionName(
        'convertFromScratchAndBindComponents',
      )

      const title = i18n.t('Converting_Dynamic_Pages_Native_Progress_Bar_Title')

      const totalSteps =
        pagesToAdd.length +
        pagesToAdd.filter(({ bindings }) => (bindings?.length ?? 0) !== 0)
          .length +
        1

      const progressBar = getProgressBar({
        editorSdkProxy,
        logger,
        totalSteps,
        title,
      })

      fedopsLogger.interactionStarted(interactionName)

      const result = await stepBuilder.getStepsForConvertingPageFromScratch({
        preset,
        pagesToAdd,
        collectionToAdd,
        pageTitleBase: pageTitle,
        isIndexable: pageData.indexable,
        progressBar,
      })

      await openPagesPanelIfNeeded({ editorSdkProxy, origin, pageRef })

      await setDynamicPageTypeStateForPage({
        editorSdkProxy,
        pageRef,
        pageType: DYNAMIC_PAGE_TYPES.ITEM_PAGE,
      })

      fedopsLogger.interactionEnded(interactionName)

      await handleFirstTimeDpConversion(
        collectionId,
        editorSdkProxy,
        experimentsManager,
      )

      appApi.openContentManager({
        collectionId,
        isModal: true,
        origin,
      })

      return result
    },
  }
}

export { duplicateDynamicPage } from './duplicate-dynamic-page/duplicateDynamicPage'
export { addBlankDynamicPage } from './add-blank-dynamic-page/addBlankDynamicPage'
export { addCategoryPage } from './add-category-page/addCategoryPage'
